import { type RefObject, useRef } from 'react'
import { createStore, type StoreApi, useStore } from 'zustand'

interface FormStoreActions {
  setFieldValue: (
    name: string,
    value?: string | number | boolean | any[],
  ) => void
  setFieldError: (name: string, value?: string) => void
  reset: () => void
}

interface FormStore {
  data: Record<string, string | number | boolean | any[] | undefined>
  errors: Record<string, string | undefined>
  actions: FormStoreActions
}

export const createFormStore = () =>
  createStore<FormStore>()((set, get) => ({
    data: {},
    errors: {},

    actions: {
      setFieldValue: (name, value) =>
        set({
          data: { ...get().data, [name]: value },
        }),

      setFieldError: (name, error: unknown) => {
        const value = error instanceof Error ? error.message : error
        set({
          errors: { ...get().errors, [name]: value as string },
        })
      },

      reset: () => set({ data: {}, errors: {} }),
    },
  }))

const useFormStore = <T,>(
  storeRef: RefObject<StoreApi<FormStore> | null>,
  selector: (state: FormStore) => T,
): T => {
  if (!storeRef.current) {
    storeRef.current = createFormStore()
  }
  return useStore(storeRef.current, selector)
}

export const useForm = () => {
  const storeRef = useRef(null)
  const data = useFormStore(storeRef, (state: FormStore) => state.data)
  const errors = useFormStore(storeRef, (state: FormStore) => state.errors)
  const { setFieldValue, setFieldError, reset } = useFormStore(
    storeRef,
    (state: FormStore) => state.actions,
  )

  return { storeRef, data, errors, setFieldError, setFieldValue, reset }
}
